export class DocumentStore {
  constructor(initialDocument = {}) {
    this.document = createDocument(initialDocument);
  }

  getDocument() {
    return structuredClone(this.document);
  }

  setDocument(nextDocument) {
    this.document = createDocument(nextDocument);
    return this.getDocument();
  }

  addDot(dot) {
    this.document.dots.push(dot);
    return dot;
  }

  addObject(object) {
    const normalizedObject = normalizeObject(object);
    normalizeRectanglePoints(normalizedObject, this.document.dots);
    this.document.objects.push(normalizedObject);
    return normalizedObject;
  }

  removeObject(objectId) {
    const objectIndex = this.document.objects.findIndex((object) => object.id === objectId);

    if (objectIndex === -1) {
      return null;
    }

    const [removedObject] = this.document.objects.splice(objectIndex, 1);
    return removedObject;
  }

  replaceObject(objectId, replacementObjects) {
    const objectIndex = this.document.objects.findIndex((object) => object.id === objectId);

    if (objectIndex === -1) {
      return null;
    }

    const normalizedObjects = replacementObjects.map((object) => {
      const normalizedObject = normalizeObject(object);
      normalizeRectanglePoints(normalizedObject, this.document.dots);
      return normalizedObject;
    });

    this.document.objects.splice(objectIndex, 1, ...normalizedObjects);
    return normalizedObjects;
  }

  getDot(dotId) {
    return this.document.dots.find((dot) => dot.id === dotId) ?? null;
  }

  getObject(objectId) {
    return this.document.objects.find((object) => object.id === objectId) ?? null;
  }

  clear() {
    this.document.dots = [];
    this.document.objects = [];
  }
}

function createDocument(initialDocument) {
  const dots = Array.isArray(initialDocument.dots) ? [...initialDocument.dots] : [];
  const objects = Array.isArray(initialDocument.objects) ? initialDocument.objects.map(normalizeObject) : [];

  normalizeRectangleObjects(objects, dots);

  return {
    version: initialDocument.version ?? 1,
    metadata: initialDocument.metadata ?? {},
    settings: initialDocument.settings ?? {},
    dots,
    objects
  };
}

function normalizeObject(object) {
  if (!object || typeof object !== "object") {
    return object;
  }

  const { dotIds, points, dimensions, ...rest } = object;
  const normalizedPoints = Array.isArray(points) ? points : Array.isArray(dotIds) ? dotIds : [];

  return {
    ...rest,
    points: [...normalizedPoints],
    dimensions: Array.isArray(dimensions) ? dimensions.map((dimension) => ({ ...dimension })) : []
  };
}

function normalizeRectangleObjects(objects, dots) {
  for (const object of objects) {
    normalizeRectanglePoints(object, dots);
  }
}

function normalizeRectanglePoints(object, dots) {
  if (object?.type !== "rectangle" || !Array.isArray(object.points) || object.points.length !== 2) {
    return;
  }

  const [firstPointId, secondPointId] = object.points;
  const firstPoint = findDot(dots, firstPointId);
  const secondPoint = findDot(dots, secondPointId);

  if (!firstPoint || !secondPoint) {
    return;
  }

  const left = Math.min(firstPoint.x, secondPoint.x);
  const right = Math.max(firstPoint.x, secondPoint.x);
  const top = Math.min(firstPoint.y, secondPoint.y);
  const bottom = Math.max(firstPoint.y, secondPoint.y);
  const originalPoints = [firstPoint, secondPoint];

  object.points = [
    getRectangleCornerPoint({ x: left, y: top }, originalPoints, object, dots, 1).id,
    getRectangleCornerPoint({ x: right, y: top }, originalPoints, object, dots, 2).id,
    getRectangleCornerPoint({ x: right, y: bottom }, originalPoints, object, dots, 3).id,
    getRectangleCornerPoint({ x: left, y: bottom }, originalPoints, object, dots, 4).id
  ];
}

function getRectangleCornerPoint(corner, originalPoints, object, dots, index) {
  const existingPoint = originalPoints.find((point) => point.x === corner.x && point.y === corner.y);

  if (existingPoint) {
    return existingPoint;
  }

  const generatedPoint = {
    id: createRectanglePointId(object, index, dots),
    x: corner.x,
    y: corner.y,
    meta: { ...(originalPoints[0].meta ?? {}) }
  };

  dots.push(generatedPoint);
  return generatedPoint;
}

function createRectanglePointId(object, index, dots) {
  const baseId = object.id ?? "rectangle";
  const dotIds = new Set(dots.map((dot) => dot.id));
  let candidate = `${baseId}-point-${index}`;
  let suffix = 2;

  while (dotIds.has(candidate)) {
    candidate = `${baseId}-point-${index}-${suffix}`;
    suffix += 1;
  }

  return candidate;
}

function findDot(dots, dotId) {
  return dots.find((dot) => dot.id === dotId) ?? null;
}
